import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from "react"; // 引入 React，确保 JSX 使用时 React 在作用域内
import "./index.less"; // 引入样式文件，用于自定义样式
import { AvatarCom } from "../../AvatarCom";
import { Menu, Popover, Skeleton } from "antd";
import { ChannelIcon, RemindIcon, SettingIcon, UserIcon } from "../../SvgIconCom/SideMenuIcon";
import { SideMenuTypes } from "remo-types/src/constants";
import { PersonalCard } from "../../PersonalCard";
import {
    globalStore,
    useChannelRoomStore,
    useChannelStore,
    useGlobalStore,
    usePreferencesStore,
} from "@obsbot/remo-stores";
import { useLocation } from "react-use";
import UserActiveSVG from "../../icons/UserActiveIcon.svg";
import MessageActiveSVG from "../../icons/MessageActiveIcon.svg";
import { RouteNameType, usePushHistory } from "@obsbot/remo-pages/src/utils/routes";
import { eventBus } from "@obsbot/remo-utils";
import { ActionType, ChannelActionModal } from "../../../components/ChannelActionModal";
import { useTranslate } from "@obsbot/remo-i18n";
import { useSafePromise } from "../../../utils/hooks";
import { IServiceVideoChat, RemoServices } from "@obsbot/remo-services";
import { PreviewCameraModal } from "../../../components/PreviewCameraModal";
export interface SideMenuProps {
    sideMenuActiveKey: SideMenuTypes;
    onSideMenuClick: (key: SideMenuTypes) => void;
    handlePersonalSetting: () => void;
    handleQuitAccount: () => void;
}
export const SideMenu: React.FC<SideMenuProps> = ({
    sideMenuActiveKey = SideMenuTypes.USER,
    onSideMenuClick,
    handlePersonalSetting,
    handleQuitAccount,
}) => {
    const t = useTranslate();
    const sp = useSafePromise();
    const services = RemoServices.getInstance();
    const [isShowCreateModal, setIsShowCreateModal] = useState(false);
    const { userInfo, rtcInfo } = useGlobalStore();
    const { hasChannels, createChannel } = useChannelStore();
    const { rtm } = useChannelRoomStore();
    const preferenceStore = usePreferencesStore(userInfo?.user_id ?? "")?.(state => state);
    const pushHistory = usePushHistory();
    const location = useLocation();
    const [selectedKeys, setSelectedKeys] = useState<string[]>([sideMenuActiveKey]); // 定义一个状态变量 selectedKey，用于存储当前选中的菜单项的键值。初始值为 "users"。
    const [isAvatarLoaded, setIsAvatarLoaded] = useState(true);
    const [localRtc, setLocalRtc] = useState<IServiceVideoChat | null>(null);
    // 是否显示进入频道的引导预览窗
    const [isShowEnterChannel, setIsShowEnterChannel] = useState(false);
    // 引导加入的频道
    const [guideChannelId, setGuideChannelId] = useState("");
    useEffect(() => {
        setSelectedKeys([sideMenuActiveKey]);
    }, [sideMenuActiveKey]);

    useEffect(() => {
        const tempActive = location?.hash?.split("/")[1];
        switch (tempActive) {
            case "friends": {
                setSelectedKeys([SideMenuTypes.USER]);
                break;
            }
            case "channel": {
                setSelectedKeys([SideMenuTypes.CHANNEL]);
                break;
            }
            case "setting": {
                setSelectedKeys([SideMenuTypes.SETTING]);
                break;
            }
            default: {
                setSelectedKeys([SideMenuTypes.USER]);
                break;
            } // 处理其他情况，例如默认选中 "users" 菜单项
        }
    }, [location]);

    const handleSelect = ({ key }: { key: string }): void => {
        const typedKey = key as SideMenuTypes;
        if (
            typedKey === SideMenuTypes.CHANNEL &&
            !hasChannels &&
            preferenceStore.hasShowedChannelGuide === false
        ) {
            setIsShowCreateModal(true);
            return;
        }
        setSelectedKeys([typedKey]);
        onSideMenuClick && onSideMenuClick(typedKey);
    };
    const handleSetting = (): void => {
        setSelectedKeys([SideMenuTypes.SETTING]);
        onSideMenuClick && onSideMenuClick(SideMenuTypes.SETTING);
    };

    const handleCreateChannel = (e: MouseEvent): void => {
        e.stopPropagation();
        if (!hasChannels && preferenceStore.hasShowedChannelGuide === false) {
            setIsShowCreateModal(true);
            return;
        }
        pushHistory(RouteNameType.ChannelPage, {});
    };

    const menuItems = useMemo(() => {
        return [
            {
                key: SideMenuTypes.USER, // 唯一的键值，用于标识每个菜单项，这里使用了一个空字符串作为 ke
                label: selectedKeys?.includes(SideMenuTypes.USER) ? (
                    <img src={UserActiveSVG} />
                ) : (
                    <UserIcon className="side-menu-user-icon" />
                ),
            },
            {
                key: SideMenuTypes.CHANNEL, // 唯一的键值，用于标识每个菜单项
                label: (
                    <div className="side-menu-channel">
                        {selectedKeys?.includes(SideMenuTypes.CHANNEL) ? (
                            <img src={MessageActiveSVG} />
                        ) : (
                            <ChannelIcon className="side-menu-channel-icon" />
                        )}
                        {!hasChannels && preferenceStore.hasShowedChannelGuide === false ? (
                            <RemindIcon
                                className="remind-channel"
                                onClick={e => handleCreateChannel(e)}
                            />
                        ) : null}
                    </div>
                ),
            },
        ];
    }, [hasChannels, selectedKeys, preferenceStore]);

    useEffect(() => {
        const handleIsLoad = (isLoad: boolean): void => {
            setIsAvatarLoaded(isLoad);
        };
        eventBus.on("load-new-avatar", handleIsLoad);
        return () => {
            eventBus.off("load-new-avatar", handleIsLoad);
        };
    }, []);

    const handleCreateChannelDone = useCallback(
        async (_channelId: string, _type: ActionType, channelName: string): Promise<void> => {
            const channelUUID = await sp(
                createChannel({
                    channelName,
                }),
            );
            if (!rtcInfo) {
                setIsShowCreateModal(false);
                return;
            }
            await rtm?.createRoom({
                uid: rtcInfo.uid,
                userSig: rtcInfo.userSig,
                roomName: channelName,
                roomUUID: channelUUID,
            });
            setGuideChannelId(channelUUID);
            setIsShowCreateModal(false);
            const rtc = await services.requestService("videoChat");
            setLocalRtc(rtc);
            setIsShowEnterChannel(true);
        },
        [rtm, rtcInfo, services, sp, createChannel],
    );

    return (
        <div className="main-layout-side-menu">
            <Menu
                className="main-layout-side-menu-com"
                items={menuItems}
                selectedKeys={selectedKeys}
                onSelect={handleSelect}
            ></Menu>
            <SettingIcon
                className={`side-menu-setting ${selectedKeys?.includes(SideMenuTypes.SETTING) ? "selected" : ""}`}
                onClick={handleSetting}
            />
            <Popover
                align={{
                    offset: [5, 5],
                }}
                color="transparent"
                content={
                    <PersonalCard
                        avatarInfo={{
                            avatar: isAvatarLoaded ? userInfo?.logo || userInfo?.avatar : "loading",
                            userID: userInfo?.user_id || "",
                            userName: userInfo?.nickname || "",
                            status: userInfo?.user_status,
                        }}
                        handlePersonalSetting={handlePersonalSetting}
                        handleQuitAccount={handleQuitAccount}
                        showSetting={true}
                    />
                }
                placement="top"
                trigger={"hover"}
            >
                <div className="side-menu-avatar">
                    {isAvatarLoaded ? (
                        <AvatarCom
                            avatar={userInfo?.logo || userInfo?.avatar}
                            status={userInfo?.user_status}
                            userID={userInfo?.user_id || ""}
                        />
                    ) : (
                        <Skeleton.Avatar active={true} shape="circle" size={50} />
                    )}
                </div>
            </Popover>
            <ChannelActionModal
                actionType={ActionType.Create}
                channelId=""
                channelName={`${t("channel.default")}1`}
                setVisible={setIsShowCreateModal}
                visible={isShowCreateModal}
                onDone={handleCreateChannelDone}
            />
            {localRtc && (
                <PreviewCameraModal
                    confirmClick={() => {
                        if (!guideChannelId) {
                            return;
                        }
                        setIsShowEnterChannel(false);
                        pushHistory(RouteNameType.ChannelRoomPage, {
                            roomUUID: guideChannelId,
                        });
                        globalStore.updateNeedShowGuide({
                            channel: true,
                            specialEffect: true,
                        });
                    }}
                    isPreview={false}
                    rtc={localRtc}
                    setVisible={setIsShowEnterChannel}
                    visible={isShowEnterChannel}
                />
            )}
        </div>
    );
};
